import { SEO } from "@/components/SEO";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle, Phone, Calendar, User, Mail, MapPin, FileText } from "lucide-react";
import { SERVICES } from "@/data/services";
import { LOCATIONS } from "@/data/locations";
import { BUSINESS } from "@/data/businessInfo";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Service", "Details", "Schedule", "Confirm"];

export default function BookingPage() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    service: "", service_type: "residential", name: "", email: "", phone: "",
    city: "", address: "", preferred_date: "", preferred_time: "", notes: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const next = () => {
    if (step === 0 && !form.service) { toast.error("Please select a service."); return; }
    if (step === 1 && (!form.name || !form.email || !form.phone)) { toast.error("Please fill in all required fields."); return; }
    setStep(s => Math.min(s + 1, 3));
  };
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setSubmitted(true);
      toast.success("Booking submitted! We'll confirm your appointment shortly.");
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-20">
        <div className="text-center max-w-md" data-testid="booking-success">
          <CheckCircle className="w-20 h-20 text-[#317E8C] mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-[#401303] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Booking Submitted</h1>
          <p className="text-[#6B3A1F] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>We'll contact you within one business day to confirm your appointment. For immediate needs, call {BUSINESS.phone}.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 btn-gradient rounded-full text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FAFAFA] min-h-[70vh] py-12 lg:py-20">
      <SEO title="Book Your Electrical Service" description="Schedule electrical service with Southern Light Solutions. Easy online booking for residential and commercial electrical work across the Treasure Coast." path="/book" />
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#401303] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Book Your Service</h1>
          <p className="mt-2 text-sm text-[#6B3A1F]" style={{ fontFamily: 'Manrope, sans-serif' }}>Fill out the form below and we'll get back to you to confirm.</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between mb-8" data-testid="booking-progress">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors duration-200 ${
                i <= step ? 'bg-[#BF8D30] text-[#401303]' : 'bg-[#6B3A1F]/15 text-[#6B3A1F]'
              }`} style={{ fontFamily: 'Outfit, sans-serif' }}>{i + 1}</div>
              <span className={`ml-2 text-xs font-semibold hidden sm:inline ${i <= step ? 'text-[#401303]' : 'text-[#6B3A1F]/50'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{s}</span>
              {i < STEPS.length - 1 && <div className={`w-8 sm:w-16 h-0.5 mx-2 ${i < step ? 'bg-[#BF8D30]' : 'bg-[#6B3A1F]/15'}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-[#6B3A1F]/10 p-6 lg:p-8" data-testid="booking-form">
          {/* Step 1: Service */}
          {step === 0 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-[#401303]" style={{ fontFamily: 'Outfit, sans-serif' }}>Select Your Service</h2>
              <div className="flex gap-3 mb-4">
                {["residential", "commercial"].map(t => (
                  <button key={t} onClick={() => setForm({...form, service_type: t})} className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-colors duration-200 ${
                    form.service_type === t ? (t === 'residential' ? 'border-[#BF8D30] bg-[#BF8D30]/10 text-[#401303]' : 'border-[#317E8C] bg-[#317E8C]/10 text-[#317E8C]') : 'border-[#6B3A1F]/20 text-[#6B3A1F]'
                  }`} style={{ fontFamily: 'Outfit, sans-serif' }} data-testid={`booking-type-${t}`}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {SERVICES.map(s => (
                  <button key={s.id} onClick={() => setForm({...form, service: s.title})} className={`text-left px-4 py-3 rounded-xl border-2 transition-colors duration-200 ${
                    form.service === s.title ? 'border-[#BF8D30] bg-[#BF8D30]/10' : 'border-[#6B3A1F]/15 hover:border-[#BF8D30]/40'
                  }`} data-testid={`booking-service-${s.id}`}>
                    <span className="text-sm font-semibold text-[#401303]" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Contact Details */}
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-[#401303]" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Details</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-[#401303] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Full Name *</label>
                  <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/20 rounded-xl focus:border-[#BF8D30] focus:outline-none text-sm" placeholder="Your name" required data-testid="booking-name" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#401303] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Email *</label>
                  <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/20 rounded-xl focus:border-[#BF8D30] focus:outline-none text-sm" placeholder="your@email.com" required data-testid="booking-email" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#401303] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Phone *</label>
                  <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/20 rounded-xl focus:border-[#BF8D30] focus:outline-none text-sm" placeholder="(772) 000-0000" required data-testid="booking-phone" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#401303] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>City</label>
                  <select value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/20 rounded-xl focus:border-[#BF8D30] focus:outline-none text-sm bg-white" data-testid="booking-city">
                    <option value="">Select your city</option>
                    {LOCATIONS.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#401303] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Address</label>
                <input type="text" value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/20 rounded-xl focus:border-[#BF8D30] focus:outline-none text-sm" placeholder="Property address" data-testid="booking-address" />
              </div>
            </div>
          )}

          {/* Step 3: Schedule */}
          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-[#401303]" style={{ fontFamily: 'Outfit, sans-serif' }}>Preferred Schedule</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-[#401303] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Preferred Date</label>
                  <input type="date" value={form.preferred_date} onChange={e => setForm({...form, preferred_date: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/20 rounded-xl focus:border-[#BF8D30] focus:outline-none text-sm" data-testid="booking-date" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#401303] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Preferred Time</label>
                  <select value={form.preferred_time} onChange={e => setForm({...form, preferred_time: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/20 rounded-xl focus:border-[#BF8D30] focus:outline-none text-sm bg-white" data-testid="booking-time">
                    <option value="">Select time</option>
                    <option>Morning (8am - 12pm)</option>
                    <option>Afternoon (12pm - 4pm)</option>
                    <option>Evening (4pm - 8pm)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#401303] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Additional Notes</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={3} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/20 rounded-xl focus:border-[#BF8D30] focus:outline-none text-sm resize-none" placeholder="Describe your project or any special instructions..." data-testid="booking-notes" />
              </div>
            </div>
          )}

          {/* Step 4: Confirm */}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-[#401303]" style={{ fontFamily: 'Outfit, sans-serif' }}>Review Your Booking</h2>
              <div className="bg-[#FAFAFA] rounded-xl p-5 space-y-3">
                {[
                  { icon: FileText, label: "Service", value: `${form.service} (${form.service_type})` },
                  { icon: User, label: "Name", value: form.name },
                  { icon: Mail, label: "Email", value: form.email },
                  { icon: Phone, label: "Phone", value: form.phone },
                  { icon: MapPin, label: "Location", value: [form.address, form.city].filter(Boolean).join(', ') || 'Not specified' },
                  { icon: Calendar, label: "Schedule", value: [form.preferred_date, form.preferred_time].filter(Boolean).join(' - ') || 'Flexible' },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-start gap-3">
                    <Icon className="w-4 h-4 text-[#BF8D30] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-[#6B3A1F]" style={{ fontFamily: 'Manrope, sans-serif' }}>{label}</p>
                      <p className="text-sm font-semibold text-[#401303]" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
                    </div>
                  </div>
                ))}
                {form.notes && (
                  <div className="flex items-start gap-3">
                    <FileText className="w-4 h-4 text-[#BF8D30] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-[#6B3A1F]">Notes</p>
                      <p className="text-sm text-[#401303]">{form.notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#6B3A1F]/10">
            {step > 0 ? (
              <button onClick={prev} className="flex items-center gap-2 text-sm font-semibold text-[#6B3A1F] hover:text-[#401303] transition-colors duration-200" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="booking-prev">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            ) : <div />}
            {step < 3 ? (
              <button onClick={next} className="flex items-center gap-2 px-6 py-3 btn-gradient rounded-full text-sm hover:bg-[#D97904] hover:text-white transition-colors duration-200" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="booking-next">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading} className="flex items-center gap-2 px-6 py-3 btn-gradient rounded-full text-sm hover:bg-[#D97904] hover:text-white transition-colors duration-200 disabled:opacity-50" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="booking-submit">
                {loading ? "Submitting..." : "Submit Booking"} <CheckCircle className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
